import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaSpinner, FaExternalLinkAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  mergeSignatureIntoPdf,
  renderContractSignaturePreview,
} from '../../utils/contractSignaturePdf';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 1rem;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  overflow-y: auto;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: #1a202c;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.25rem;
  cursor: pointer;
  color: #718096;
`;

const Steps = styled.ol`
  margin: 0 0 1rem;
  padding-left: 1.2rem;
  color: #2d3748;
  line-height: 1.6;

  li {
    margin-bottom: 0.35rem;
  }
`;

const PreviewSection = styled.div`
  margin-bottom: 1rem;
`;

const PreviewTitle = styled.p`
  margin: 0 0 0.5rem;
  font-weight: 600;
  color: #2d3748;
  font-size: 0.95rem;
`;

const PreviewFrame = styled.div`
  width: 100%;
  min-height: 200px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  canvas {
    display: block;
    max-width: 100%;
    height: auto;
  }
`;

const PreviewLoading = styled.div`
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #718096;
  gap: 0.5rem;
`;

const OpenPdfLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #4299e1;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const PadLabel = styled.p`
  margin: 0 0 0.5rem;
  font-weight: 600;
  color: #2d3748;
  font-size: 0.95rem;
`;

const CanvasWrapper = styled.div`
  border: 2px dashed #4299e1;
  border-radius: 8px;
  background: #f7fafc;
  margin-bottom: 1rem;
  touch-action: none;
  position: relative;

  canvas {
    display: block;
    width: 100%;
    height: 160px;
    cursor: crosshair;
  }
`;

const CanvasHint = styled.span`
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 0.65rem;
  font-size: 0.8rem;
  color: #a0aec0;
  pointer-events: none;
  text-align: center;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Button = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0.65rem 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: #edf2f7;
  color: #2d3748;
`;

const PrimaryButton = styled(Button)`
  background: #4299e1;
  color: #fff;
`;

function trimCanvas(canvas) {
  const context = canvas.getContext('2d');
  const { width, height } = canvas;
  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;

  let top = height;
  let left = width;
  let right = 0;
  let bottom = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 0) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }

  if (right < left || bottom < top) {
    return null;
  }

  const trimmedWidth = right - left + 1;
  const trimmedHeight = bottom - top + 1;
  const trimmed = document.createElement('canvas');
  trimmed.width = trimmedWidth;
  trimmed.height = trimmedHeight;
  trimmed.getContext('2d').drawImage(
    canvas,
    left,
    top,
    trimmedWidth,
    trimmedHeight,
    0,
    0,
    trimmedWidth,
    trimmedHeight
  );
  return trimmed;
}

function drawSignatureGuide(context, canvas) {
  context.save();
  context.strokeStyle = '#cbd5e0';
  context.lineWidth = 1;
  context.setLineDash([6, 4]);
  context.beginPath();
  context.moveTo(24, canvas.height - 36);
  context.lineTo(canvas.width - 24, canvas.height - 36);
  context.stroke();
  context.restore();
}

const ContractSignatureModal = ({ contractUrl, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const canvas = previewCanvasRef.current;
    if (!canvas) return undefined;

    (async () => {
      try {
        setPreviewLoading(true);
        setPreviewError(false);
        await renderContractSignaturePreview(
          contractUrl,
          canvas,
          t('agencyOptions.signHereLabel')
        );
        if (!cancelled) setPreviewLoading(false);
      } catch (error) {
        console.error('Contract preview render failed:', error);
        if (!cancelled) {
          setPreviewLoading(false);
          setPreviewError(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contractUrl, t]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.floor(rect.width);
      canvas.height = 160;
      context.strokeStyle = '#1a202c';
      context.lineWidth = 2;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      drawSignatureGuide(context, canvas);
    };

    resize();
    window.addEventListener('resize', resize);

    const getPoint = (event) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const start = (event) => {
      event.preventDefault();
      drawingRef.current = true;
      const point = getPoint(event);
      context.beginPath();
      context.moveTo(point.x, point.y);
    };

    const move = (event) => {
      if (!drawingRef.current) return;
      event.preventDefault();
      const point = getPoint(event);
      context.lineTo(point.x, point.y);
      context.stroke();
      hasInkRef.current = true;
    };

    const end = () => {
      drawingRef.current = false;
    };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []);

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    drawSignatureGuide(context, canvas);
    hasInkRef.current = false;
  };

  const handleSubmit = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current) {
      toast.warn(t('agencyOptions.signContractEmpty'));
      return;
    }

    const trimmed = trimCanvas(canvas);
    if (!trimmed) {
      toast.warn(t('agencyOptions.signContractEmpty'));
      return;
    }

    try {
      setSubmitting(true);
      const signaturePng = trimmed.toDataURL('image/png');
      const pdfBytes = await mergeSignatureIntoPdf(contractUrl, signaturePng);
      await onSubmit(pdfBytes);
    } catch (error) {
      console.error('Contract sign submit failed:', error);
      const message = error?.message || t('agencyOptions.signContractError');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <h3>{t('agencyOptions.signContractTitle')}</h3>
          <CloseButton type="button" onClick={onClose} aria-label="close">
            <FaTimes />
          </CloseButton>
        </Header>

        <Steps>
          <li>{t('agencyOptions.signStep1')}</li>
          <li>{t('agencyOptions.signStep2')}</li>
          <li>{t('agencyOptions.signStep3')}</li>
        </Steps>

        <PreviewSection>
          <PreviewTitle>{t('agencyOptions.signContractPreviewTitle')}</PreviewTitle>
          <PreviewFrame>
            {previewLoading ? (
              <PreviewLoading>
                <FaSpinner className="spinner" />
                {t('agencyOptions.signPreviewLoading')}
              </PreviewLoading>
            ) : null}
            <canvas ref={previewCanvasRef} style={{ display: previewLoading ? 'none' : 'block' }} />
          </PreviewFrame>
          {previewError ? (
            <OpenPdfLink href={contractUrl} target="_blank" rel="noopener noreferrer">
              <FaExternalLinkAlt />
              {t('agencyOptions.openFullContract')}
            </OpenPdfLink>
          ) : (
            <OpenPdfLink href={contractUrl} target="_blank" rel="noopener noreferrer">
              <FaExternalLinkAlt />
              {t('agencyOptions.openFullContract')}
            </OpenPdfLink>
          )}
        </PreviewSection>

        <PadLabel>{t('agencyOptions.signContractPadLabel')}</PadLabel>
        <CanvasWrapper>
          <canvas ref={canvasRef} />
          <CanvasHint>{t('agencyOptions.signContractPadHint')}</CanvasHint>
        </CanvasWrapper>

        <Actions>
          <SecondaryButton type="button" onClick={handleClear} disabled={submitting}>
            {t('agencyOptions.clearSignature')}
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <FaSpinner className="spinner" /> : null}
            {submitting ? t('agencyOptions.signingContract') : t('agencyOptions.confirmSignature')}
          </PrimaryButton>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default ContractSignatureModal;
